import { defineMessage } from '@formatjs/intl';
import { socialExport } from '@mi-front/api-clients';
import { Grid, Stack, Tooltip, Typography } from '@mui/material';
import { SwitchElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { TiktokPrivacyField } from './TiktokPrivacyField';

const disabledByTiktokPrivacyTooltipMessage = defineMessage({
  id: 'socialExport.tiktok.settings.disabledByTiktokPrivacyTooltip',
  defaultMessage: 'Desactivado en la configuración de privacidad de tu cuenta de TikTok.',
});

interface TikTokSettingsFieldsProps {
  /** Creator info from API; when provided, privacy options and interaction toggles follow it (TikTok guidelines). */
  creatorInfo?: socialExport.TikTokCreatorInfoDto | null;
}

/**
 * Renders TikTok settings: privacy level (from creator_info) and Comment/Duet/Stitch toggles.
 * When creatorInfo is provided, options come from privacy_level_options and disabled toggles are greyed out.
 */
export function TikTokSettingsFields({ creatorInfo }: TikTokSettingsFieldsProps) {
  const { formatMessage, $t } = useIntl();
  const commentDisabled = creatorInfo?.comment_disabled ?? false;
  const duetDisabled = creatorInfo?.duet_disabled ?? false;
  const stitchDisabled = creatorInfo?.stitch_disabled ?? false;

  return (
    <Stack spacing={3}>
      <Grid container>
        <Grid item xs={12} md={8} lg={6}>
          <TiktokPrivacyField creatorInfo={creatorInfo} />
        </Grid>
      </Grid>
      <Stack spacing={2}>
        <Typography component="span" variant="subtitle2">
          {$t({
            id: 'socialExport.tiktok.settings.allowUsersTo',
            defaultMessage: 'Permitir a los usuarios ',
          })}
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Tooltip
            title={commentDisabled ? formatMessage(disabledByTiktokPrivacyTooltipMessage) : ''}
          >
            <span>
              <SwitchElement
                name="allowComment"
                label={$t({
                  id: 'socialExport.tiktok.settings.allowComment',
                  defaultMessage: 'Comentar',
                })}
                disabled={commentDisabled}
              />
            </span>
          </Tooltip>
          <Tooltip title={duetDisabled ? formatMessage(disabledByTiktokPrivacyTooltipMessage) : ''}>
            <span>
              <SwitchElement
                name="allowDuet"
                label={$t({
                  id: 'socialExport.tiktok.settings.allowDuet',
                  defaultMessage: 'Duet',
                })}
                disabled={duetDisabled}
              />
            </span>
          </Tooltip>
          <Tooltip
            title={stitchDisabled ? formatMessage(disabledByTiktokPrivacyTooltipMessage) : ''}
          >
            <span>
              <SwitchElement
                name="allowStitch"
                label={$t({
                  id: 'socialExport.tiktok.settings.allowStitch',
                  defaultMessage: 'Stitch',
                })}
                disabled={stitchDisabled}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
}
